import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { defineApp, history, Link, useModel } from '@umijs/max';
import { Badge } from 'antd';
import React, { useEffect, useState } from 'react';

import {
  AvatarDropdown,
  AvatarName,
  Chatbot,
  ChatbotAction,
  Footer,
} from '@/components';
import { BASE_URL } from '@/services/config';
import { getUnreadReportCount } from '@/services/report';
import { currentUser as queryCurrentUser } from '@/services/user';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

import DefaultAvatar from '@/assets/default-avatar.png';
import NotificationCenter from '@/components/NotificationCenter';
// import PageBG1 from '@/assets/page-bg-1.png';
// import PageBG2 from '@/assets/page-bg-2.png';
import VersionModal from '@/components/VersionModal';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.USER.User;
  unreadReports?: number;
  token?: string;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.USER.User | undefined>;
  fetchUnreadReportCount?: () => Promise<number>;
}> {
  const fetchUserInfo = async () => {
    try {
      const userInfo = await queryCurrentUser();
      return userInfo;
    } catch (_error) {
      const { location } = history;
      if (location.pathname !== loginPath) {
        const searchParams = new URLSearchParams({
          redirect: location.pathname + location.search,
        });
        history.push({
          pathname: loginPath,
          search: searchParams.toString(),
        });
      }
    }
    return undefined;
  };
  const fetchUnreadReportCount = async () => {
    const res = await getUnreadReportCount();
    return res.data;
  };
  const unreadReports = await fetchUnreadReportCount();
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      fetchUnreadReportCount,
      currentUser,
      unreadReports,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    fetchUnreadReportCount,
    unreadReports,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [currrentMenuCollapsed, setCurrentMenuCollapsed] = useState(false);
  const [unreadReportCount, setUnreadReportCount] = useState(0);
  const { chatbotOpen, openChatbot, closeChatbot } = useModel('chatbot');
  const ws = useModel('websocket');

  useEffect(() => {
    ws.connect();
  }, []);

  useEffect(() => {
    if (initialState?.unreadReports) {
      setUnreadReportCount(initialState?.unreadReports);
    }
  }, [initialState?.unreadReports]);

  return {
    actionsRender: () => [
      <ChatbotAction key="chatbot" onClick={() => openChatbot()} />,
      <NotificationCenter key="notification" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar || DefaultAvatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.nickName,
    //   font: {
    //     color: 'hsla(0, 0%, 0%, 0.05)',
    //   },
    // },
    menuItemRender: (menuItemProps, defaultDom) => {
      let extra: React.ReactNode = null;
      if (menuItemProps.path === '/reports') {
        extra =
          unreadReportCount > 0 ? (
            <Badge
              dot
              styles={{
                root: { position: 'absolute', right: '20%', top: 10 },
              }}
            />
          ) : null;
      }
      if (
        menuItemProps.path &&
        location.pathname !== menuItemProps.path &&
        !menuItemProps.isUrl &&
        !menuItemProps.children
      ) {
        return (
          <Link
            to={menuItemProps.path}
            target={menuItemProps.target}
            onClick={() => {
              history.push(menuItemProps.path!); // 添加路由跳转逻辑
            }}
          >
            {defaultDom}
            {extra}
          </Link>
        );
      }
      return (
        <>
          {defaultDom}
          {extra}
        </>
      );
    },
    footerRender: (props) => <Footer menuCollapsed={props.collapsed} />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      // if (!initialState?.currentUser && location.pathname !== loginPath) {
      //   const searchParams = new URLSearchParams({
      //     redirect: location.pathname + location.search,
      //   });
      //   history.push({
      //     pathname: loginPath,
      //     search: searchParams.toString(),
      //   });
      // }

      // 如果跳转到登录页，清理所有 WebSocket 连接
      if ([loginPath].includes(location.pathname)) {
        ws.disconnectAll();
      }

      // 指定页面收起侧边菜单栏
      if (location.pathname.startsWith('/robot/')) {
        setMenuCollapsed(true);
      } else {
        setMenuCollapsed(currrentMenuCollapsed);
      }
    },
    collapsed: menuCollapsed,
    onCollapse: (collapsed) => {
      setMenuCollapsed(collapsed);
      setCurrentMenuCollapsed(collapsed);
    },
    // bgLayoutImgList: [
    //   {
    //     src: PageBG2,
    //     left: 85,
    //     bottom: 100,
    //     height: '303px',
    //   },
    //   {
    //     src: PageBG2,
    //     bottom: -68,
    //     right: -45,
    //     height: '303px',
    //   },
    //   {
    //     src: PageBG1,
    //     bottom: 0,
    //     left: 0,
    //     width: '331px',
    //   },
    // ],
    links: isDev
      ? [
          <a
            key="api-doc"
            href="https://docs.apipost.net/docs/detail/50b02682e891000?target_id=2bdd8997f82003"
            target="_blank"
            rel="noopener"
          >
            <LinkOutlined />
            <span>API文档</span>
          </a>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
          <Chatbot show={chatbotOpen} onClose={() => closeChatbot()} />
        </>
      );
    },
    headerContentRender: () => {
      return <VersionModal />;
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: BASE_URL,
  ...errorConfig,
};

export default defineApp({
  layout,
  getInitialState,
  request,
});
